import React, { useEffect, useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import Errors from '../common/Errors';
import Icon from '../common/Icon';
import {
  FieldContext,
  OptionsContext,
  MetadataEntryContext,
} from '../../hooks/Contexts';
import { DividedRow, Half, Third } from '../common/RowLengths';
import {
  exportOptionsTemplate,
  handleCreateOption,
  createFromCSV,
  handleDeleteAllOptions,
  updateFromCSV,
} from './constants/handleOption';
import CSVUploadTipsModal from './CSVUploadTipsModal';
import { downloadAllEvents } from '../metadata/constants/handleCalendarEvents';
import { confirmation, notify } from '../../helpers/notifications';
import { getFilenameFromHeader } from '../../helpers/filename-from-header';
import { downloadBlob } from '../../helpers/downloadBlob';

const NewOption = () => {
  const {
    register,
    watch,
    setValue,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({ defaultValues: { name: '', value: '' }, criteriaMode: 'all' });
  const { options, setOptions, determineSort } = useContext(OptionsContext);
  const { field } = useContext(FieldContext);
  const metaEntries = useContext(MetadataEntryContext);
  const [showTips, setShowTips] = useState(false);
  const calendarAvailable = options.some((o) => o.calendar_event);

  useEffect(() => {
    if (document.activeElement.name == 'name') {
      setValue('value', watch('name'), { shouldDirty: true });
    }
  }, [watch('name')]);

  const onSubmit = async (formData) => {
    const field_id = field.id;
    const sort = options.length;
    const hidden = formData.local_quota === 0 ? true : false;
    let option = { ...formData, field_id, sort, hidden };
    let notify = true;
    let created = await handleCreateOption(option, notify);
    if (created.ok) {
      setOptions(determineSort([...options, created.data]));
      reset({ name: '', value: '' });
    } else if (created.errors) {
      Object.entries(created.errors).forEach(([key, message]) =>
        setError(key, { types: { [key]: message } })
      );
    }
  };

  const handleDeleteAll = async (e) => {
    e.preventDefault();
    await handleDeleteAllOptions(options.map((o) => o.id));
  };

  const handleExportTemplate = (e) => {
    e.preventDefault();
    let includeIcalColumns = calendarAvailable;
    const exportAndCleanup = () => {
      exportOptionsTemplate(
        { field, options, metaEntries },
        includeIcalColumns
      );
      notify.success();
    };
    if (!includeIcalColumns && ['select', 'radio'].includes(field.type)) {
      confirmation.acknowledge(
        function yesClicked() {
          includeIcalColumns = true;
          exportAndCleanup();
        },
        function noClicked() {
          exportAndCleanup();
        },
        'Add ical columns to template?',
        "If you intend to generate and send ICS calendar attachments based on this field, click 'Yes'. Otherwise, disregard this message by clicking 'No'."
      );
    } else {
      exportAndCleanup();
    }
  };

  const handleCalendarEvents = async () => {
    confirmation.prompt(
      'Allow Notifications?',
      'Enter your email to be notified of events that have been set to send alerts or leave blank to ignore alerts:',
      '',
      async (email) => {
        const ics = await downloadAllEvents(field.id, email);
        if (ics.ok) {
          let cd = ics.headers['content-disposition'];
          let filename =
            getFilenameFromHeader(cd) || 'TXSSC: Unknown Calendar.ics';
          downloadBlob(new Blob([ics.data]), filename);
        }
      }
    );
  };

  return (
    <div className='creation-form'>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset>
          <DividedRow>
            <Half title='Display Name:'>
              <input
                id='option-name'
                placeholder='Yes!'
                {...register('name', {
                  required: 'Required',
                  deps: 'value',
                })}
              />
              <ErrorMessage
                errors={errors}
                name='name'
                render={({ messages }) => <Errors messages={messages} />}
              />
            </Half>
            <Half title='Value (will be displayed on submission):'>
              <input
                id='option-value'
                placeholder='yes'
                {...register('value', { required: 'Required' })}
              />
              {field.validator == 'date' && (
                <p style={{ fontSize: '14px' }}>
                  <i>MM/DD/YYYY format for proper Submissions sorting</i>
                </p>
              )}
              <ErrorMessage
                errors={errors}
                name='value'
                render={({ messages }) => <Errors messages={messages} />}
              />
            </Half>
          </DividedRow>

          {field.informs_global && (
            <div className='row'>
              <Third labelFor='local-quota' title='Option Quota:'>
                <input
                  id='local-quota'
                  type='number'
                  min={0}
                  {...register('local_quota', {
                    shouldUnregister: true,
                    value: 0,
                  })}
                />
              </Third>
            </div>
          )}
        </fieldset>

        <div className='controls'>
          <button type='submit'>
            Save
            <Icon icon='save' />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              createFromCSV({ field, options, metaEntries });
            }}
            type='button'
            className='blue'
          >
            <Icon icon='file-csv' /> Create
          </button>
          <input type='file' id='csvCreate' />

          <button
            onClick={(e) => {
              e.preventDefault();
              updateFromCSV({ field, options, metaEntries });
            }}
            type='button'
            className='blue'
          >
            <Icon icon='file-csv' /> Update
          </button>
          <input type='file' id='csvUpdate' />

          <Icon
            title='CSV Uploading Tips'
            icon='question-circle'
            onClick={(e) => {
              e.preventDefault();
              setShowTips(true);
            }}
          />
          <CSVUploadTipsModal
            show={showTips}
            close={() => setShowTips(false)}
          />

          <Icon
            title='Export CSV'
            icon='download'
            onClick={handleExportTemplate}
          />

          {calendarAvailable && (
            <Icon
              title='Download Calendar Events'
              icon='calendar-days'
              onClick={handleCalendarEvents}
            />
          )}

          <button
            className='right danger'
            type='button'
            onClick={(e) => handleDeleteAll(e)}
            disabled={options.length == 0}
          >
            Delete All
            <Icon icon='trash' />
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewOption;
